import {collection, query, doc, getDocs, onSnapshot, getDocsFromCache, getDoc, documentId} from 'firebase/firestore';
import {where} from '-/data/firestoreQuery';
import get from 'lodash/get';
import chunk from 'lodash/chunk';
import uniq from 'lodash/uniq';
import castArray from 'lodash/castArray';
import isString from 'lodash/isString';
import axios from 'axios';
import {addBreadcrumb} from '@sentry/react';
import {ShortRanks, FirefighterRanks, Ranks, ensureMomentDates} from '@embertracking/common';


import {app, apiUrl, auth} from '-/firebase';
import {reportError, resilientSnapshot, isOfflineError} from '-/data/errorReporting';
import {assertUploadWithinLimit} from '-/data/uploadLimit';
import {expireSession} from '-/data/session';
import {HUB_SESSION_EXPIRED_CODE, HUB_SESSION_ENDED_MESSAGE} from '-/data/hubSession';
import {MAINTENANCE_CODE, reportMaintenance} from '@embertracking/ui/data/maintenance';

const projectId = get(app, '_options.projectId');

let cachedUsers = {};

// The incident-type swatch palette moved into
// `@embertracking/common/data/reports/incidentTypeColors` for #1719: the report
// models emit a `color` on every type row, and the hub renders those rows, so
// the mapping has to be reachable from a package neither app owns. Re-exported
// here because ~30 call sites import it from `-/data/utils`.
export {colorForIncidentType} from '@embertracking/common/data/reports/incidentTypeColors.mjs';

const fetchDocumentsWithIds = async(db, ref, ids, returnObject) => {
    const data = returnObject ? {} : [];

    if (!ids || !ids.length) {
        return data;
    }

    // Separate string IDs from object entries
    const stringIds = [];
    const objectEntries = [];

    ids.forEach(id => {
        if (typeof id === 'string') {
            stringIds.push(id);
        } else if (typeof id === 'object') {
            objectEntries.push(id);
        }
    });

    // Add object entries directly to the result
    if (returnObject) {
        objectEntries.forEach(obj => {
            data[obj.id] = obj;
        });
    } else {
        data.push(...objectEntries);
    }

    // Fetch documents only for string IDs. Firestore's `in` operator caps at 10
    // ids per query, so we chunk. The chunks are independent — fire them in
    // parallel so total wall time is one round trip's worth, not N.
    if (stringIds.length) {
        const chunks = chunk(stringIds, 10);
        const snapshots = await Promise.all(chunks.map(c => getDocs(query(ref, where(documentId(), 'in', c)))));

        for (const raw of snapshots) {
            if (!raw) continue;
            raw.forEach(item => {
                const doc = processRawDoc(item);
                const {id} = doc;

                if (returnObject) {
                    data[id] = doc;
                } else {
                    data.push(doc);
                }
            });
        }
    }

    return data;
};

let userKeys = ['user', 'users', 'members', 'instructors', 'assignee', 'completedBy', 'officerInCharge', 'cancelled', 'standby', 'memberUid'];
export const populateDocsWithUsers = (incoming, users) => {
    const userMap = Array.isArray(users) ? users.reduce((acc, user) => {
        acc[user.uid] = user;
        return acc;
    }, {}) : users;

    const undefinedUsers = Object.keys(userMap).filter(userId => !userMap[userId]);
    if (undefinedUsers.length) {
        console.warn('populateDocsWithUsers: undefined users', undefinedUsers);
    }

    const docs = castArray(incoming).filter(Boolean);
    const result = docs.map(doc => {
        for (const key of userKeys) {
            const existing = doc[key];
            if (!existing) {
                continue;
            }

            if (Array.isArray(existing)) {
                doc[key] = existing.map(userId => userMap[userId] || {
                    id: userId,
                    uid: userId
                });
            } else {
                doc[key] = userMap[existing] || {
                    id: existing,
                    uid: existing
                };
            }
        }

        return doc;
    });

    if (Array.isArray(incoming)) {
        return result.filter(Boolean);
    }

    return result[0];
};

export const populateUsers = async(db, incoming) => {
    let userIds;
    const docs = castArray(incoming).filter(Boolean);
    for (const doc of docs) {
        const users = userKeys.map(key => doc[key]).filter(Boolean).flat().map(user => {
            if (typeof user === 'object') {
                return user.uid;
            }

            return user;
        });

        userIds = userIds ? [...userIds, ...users] : users;
    }

    const users = await populateCollectionIds(db, 'usersLight', userIds);
    return populateDocsWithUsers(Array.isArray(incoming) ? docs : docs[0], users);
};

export const populateKeyFromCollection = async(db, incoming, key, collectionName) => {
    const isArray = Array.isArray(incoming);
    const docs = castArray(incoming);
    const ids = docs.map(doc => doc[key]).filter(Boolean).flat();

    const ref = isString(collectionName) ? collectionName : collection(db, collectionName);

    // This is a DISPLAY join — swap ids for labels on records the caller has
    // already been allowed to read. The referenced collection can be gated
    // separately (a member with incidents.read but apparatus.read denied is a
    // real role, see NORTHSHOREFD-6970A-6), and letting that denial propagate
    // took whole pages down with it: /incidents rendered an empty grid and
    // toasted the raw rules error ("false for 'list' @ L65") at the member,
    // over incident data they were perfectly entitled to see.
    //
    // So a denial degrades to "no labels" — the key is emptied rather than left
    // holding bare ids, since callers render `get(doc, 'tag') || 'Unknown'` and
    // a row of "Unknown" is worse than an omitted line. Breadcrumb, not a Sentry
    // event: the rules denied this on purpose, and it stays visible in whatever
    // report a genuine failure produces later.
    let data;
    let denied = false;
    try {
        data = await populateCollectionIds(db, ref, ids);
    } catch (e) {
        if (e?.code !== 'permission-denied') {
            throw e;
        }
        addBreadcrumb({
            category: 'firestore',
            level: 'warning',
            message: `populateKeyFromCollection: permission-denied reading ${isString(collectionName) ? key : collectionName}`
        });
        data = {};
        denied = true;
    }

    const result = docs.map(doc => {
        const id = doc[key];
        if (Array.isArray(id)) {
            doc[key] = denied ? [] : id.map(uid => data[uid]);
        } else {
            doc[key] = denied ? undefined : data[id];
        }

        return doc;
    });

    return isArray ? result : result[0];
};

export const populateCollectionIds = async(db, ref, ids) => {
    if (typeof ref === 'string') {
        ref = collection(db, ref);
    }

    const toFetch = uniq(ids).filter(id => !cachedUsers[id]);
    if (toFetch.length) {
        const fetched = await fetchDocumentsWithIds(db, ref, toFetch, true);

        cachedUsers = {
            ...cachedUsers,
            ...fetched
        };
    }

    return uniq(ids).reduce((result, userId) => {
        result[userId] = cachedUsers[userId];
        return result;
    }, {});
};

const getUserRankLabel = user => {
    if (!user) {
        return;
    }

    const {role} = user || {};
    if (!FirefighterRanks.includes(role)) {
        return;
    }

    return ShortRanks[role];
};

export const processUserName = (user, opts) => {
    const {users} = opts || {};

    const firstName = user.firstName?.trim();
    const lastName = user.lastName?.trim();
    const fullName = [firstName || '', lastName || ''].filter(Boolean).join(' ');
    const rank = getUserRankLabel(user);
    let shortName, rankedShortName;

    if (users.length) {
        const uniqueLastName = users.filter(doc => doc.lastName === lastName).length === 1;
        shortName = uniqueLastName ? lastName : [firstName && firstName[0], lastName].filter(Boolean).join('. ');

        if (rank) {
            rankedShortName = [rank, lastName].filter(Boolean).join(' ');
        }
    }

    return {
        ...user,
        firstName,
        lastName,
        fullName,
        rankedFullName: [rank, fullName].filter(Boolean).join(' '),
        ...(shortName && {
            shortName,
            rankedShortName
        })
    };
};

export const processRawDoc = raw => {
    if (!raw || !raw.exists()) {
        return null;
    }

    const {id} = raw;

    let data = raw.data();
    delete data.ref;
    data = ensureMomentDates(data);

    return {
        ...data,
        id,
        uid: id
    };
};

export const processRawDocs = (raw, opts = {}) => {
    const {includePath = false} = opts;
    let docs = [];

    raw.forEach(doc => {
        const processed = processRawDoc(doc);
        if (processed) {
            docs.push({
                ...processed,
                ...(includePath && {
                    path: doc.ref.path
                })
            });
        }
    });

    return docs;
};

export const getSnapshot = (db, collectionName, fn, onError) => {
    if (onError) {
        return onSnapshot(collection(db, collectionName), snapshot => {
            fn(processRawDocs(snapshot));
        }, onError);
    }

    // resilientSnapshot recovers the listener from the wake-from-sleep
    // expired-token permission-denied instead of leaving it dead until the
    // next page load (and only reports genuine rules denials to Sentry).
    return resilientSnapshot(
        errorHandler => onSnapshot(collection(db, collectionName), snapshot => {
            fn(processRawDocs(snapshot));
        }, errorHandler),
        `getSnapshot.${collectionName}`
    );
};

export const getCollection = async(db, collectionName, opts) => {
    const {useCache = false} = opts || {};
    const q = isString(collectionName) ? query(collection(db, collectionName)) : collectionName;

    let raw = await getDocsFromCache(q);
    if (!useCache || raw.empty) {
        raw = await getDocs(q);
    }
    
    return processRawDocs(raw);
};

export const getCollectionDoc = async(db, collectionName, uid) => {
    if (!collectionName || !uid) {
        return;
    }

    const ref = doc(db, collectionName, uid);
    const raw = await getDoc(ref);
    return processRawDoc(raw);
};

export const hasPermission = (user, permission) => {
    if (!user || typeof user === 'string') {
        return false;
    }

    const {permissions = {}} = user;
    if (permissions.admin) {
        return true;
    }

    return permissions[permission];
};

// Moved to @embertracking/ui so the hub renders the same qualification cells.
// Re-exported here because this module is where the rest of the app has always
// imported them from — the move shouldn't ripple through call sites.
export {SkillStages} from '@embertracking/ui/data/skills';

// In dev, when the page is loaded from a non-loopback host (e.g. ngrok), the
// browser can't reach the local api port directly — route through the Vite
// proxy at /__/api so a single forwarded port covers the whole local stack.
const devApiBase = () => {
    // REACT_APP_FORCE_EMULATOR_PROXY (set by the #734 dev container) routes the
    // api through the same-origin /__/api proxy even on localhost, because the
    // api runs inside the container on a port the browser can't reach directly.
    const forceProxy = process.env.REACT_APP_FORCE_EMULATOR_PROXY === 'true';
    if (typeof window !== 'undefined' && (forceProxy || !['localhost', '127.0.0.1', ''].includes(window.location.hostname))) {
        return `${window.location.origin}/__/api`;
    }
    // bin/start.js injects REACT_APP_API_BASE_URL with the correct port for
    // the current worktree offset (api is 5002 + 100*N).
    return process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:5002';
};

// Non-throwing variant of resolveApiBase used for public read-only endpoints
// (e.g. /releaseNotes) where a missing apiUrl should skip the feature, not
// surface as an error or trigger the stale-cache reload path in resolveApiBase.
export const getApiBase = () => {
    const isDev = process.env.NODE_ENV === 'development';
    const usingApiKey = process.env.REACT_APP_API_KEY;
    if (isDev && !usingApiKey) {
        return devApiBase().replace(/\/$/, '');
    }
    return apiUrl ? apiUrl.replace(/\/$/, '') : null;
};

// Exported for direct-fetch callers (e.g. viewFile, which streams a file blob
// and can't use callFunction). Returns the api base URL (with trailing slash)
// and will trigger a one-shot cache-bust reload when apiUrl is missing due to
// stale firebase-config.
export const resolveApiBase = () => {
    const isDev = process.env.NODE_ENV === 'development';
    const usingApiKey = process.env.REACT_APP_API_KEY;

    if (isDev && !usingApiKey) {
        return `${devApiBase().replace(/\/$/, '')}/`;
    }
    if (!apiUrl) {
        // apiUrl missing here is almost always stale-cache: the new bundle
        // shipped but the browser is serving a pre-cloudrun firebase-config.js
        // from its prior max-age=3600 copy. Force a one-shot cache-busting
        // reload to self-heal. sessionStorage guards against an infinite loop
        // when apiUrl is genuinely missing from the server-side config.
        const FLAG = 'apiUrl_cache_bust_attempted';
        if (typeof window !== 'undefined' && window.sessionStorage && !sessionStorage.getItem(FLAG)) {
            sessionStorage.setItem(FLAG, '1');
            const url = new URL(window.location.href);
            url.searchParams.set('_cb', Date.now().toString());
            window.location.replace(url.toString());
            throw new Error('Reloading to refresh stale config cache...');
        }
        throw new Error('apiUrl must be set in the project\'s firebase-configs/*.json (surfaced as window.__FIREBASE_CONFIG__.apiUrl) to call the api');
    }
    return `${apiUrl.replace(/\/$/, '')}/`;
};

const SENSITIVE_KEYS = new Set(['password', 'token', 'secret', 'apiKey', 'accessToken', 'refreshToken', 'idToken', 'authorization']);

const redactSensitive = params => {
    if (!params || typeof params !== 'object') return params;
    if (Array.isArray(params)) return params.map(redactSensitive);
    const out = {};
    for (const [key, value] of Object.entries(params)) {
        out[key] = SENSITIVE_KEYS.has(key) ? '[REDACTED]' : (typeof value === 'object' ? redactSensitive(value) : value);
    }
    return out;
};

export const callFunction = async (path, params, opts) => {
    const requestUser = auth.currentUser;
    try {
        const base = resolveApiBase();
        const url = `${base}${path}`;

        const {headers, ...rest} = opts || {};

        const requestHeaders = {...headers};
        if (requestUser) {
            const token = await requestUser.getIdToken();
            requestHeaders.Authorization = `Bearer ${token}`;
        }
        // The api is multi-tenant — include the Firebase project id so the
        // server can scope data access to the right tenant. Authed requests
        // get this from the token's `aud`; public requests (loginAsStation)
        // rely on this header.
        if (projectId) {
            requestHeaders['X-Project-Id'] = projectId;
        }

        const {data} = await axios.post(url, params, {
            headers: requestHeaders,
            ...rest
        });

        return data;
    } catch(e) {
        // With responseType:'blob' (the qualification/transcript zip exports)
        // axios hands back the error body as a Blob, so the usual
        // response.data.error lookups all miss and every failure surfaced as a
        // generic "Request failed with status code 500". Read it back as text
        // and pull the real message out first.
        let data = get(e, 'response.data');
        if (typeof Blob !== 'undefined' && data instanceof Blob) {
            try {
                data = JSON.parse(await data.text());
            } catch { /* not JSON — fall through to e.message */ }
        }

        const message = get(data, 'error.message') || get(data, 'error') || e.message;
        const error = new Error(message);

        // Carry the underlying error's code onto the wrapper. Sentry's
        // beforeSend filter (isOfflineError, wired up in App.js) recognises
        // transient network failures by `code`, so a bare Error is invisible to
        // it — which is how "Firebase: Error (auth/network-request-failed)"
        // from the token refresh above kept reaching Sentry after #784 filtered
        // the raw FirebaseError. Also lets callers branch on the real code.
        if (e?.code) {
            error.code = e.code;
        }
        // The api's own error code (`{error: {code}}` from errorMiddleware /
        // the auth middlewares) — distinct from the axios/Firebase code above.
        // Lets App.js recognise a signed-out state such as an expired operator
        // session without matching on message text.
        const serverCode = get(data, 'error.code');
        if (serverCode) {
            error.serverCode = serverCode;
        }
        // The api is closed for maintenance (#302): MaintenanceGate takes over
        // the screen. Not reported to Sentry — it's a planned state.
        if (serverCode === MAINTENANCE_CODE) {
            reportMaintenance(message);
        }
        if (['session-ended', 'operator-session-expired', HUB_SESSION_EXPIRED_CODE].includes(serverCode) && requestUser && auth.currentUser === requestUser) {
            await expireSession(serverCode === 'operator-session-expired' ? 'Your operator session has ended.'
                : serverCode === HUB_SESSION_EXPIRED_CODE ? HUB_SESSION_ENDED_MESSAGE
                    : 'Your session has ended. Please sign in again.');
        }

        // Skip Sentry for expected errors: 4xx (validation/auth/permission) and
        // network failures (offline, DNS, CORS). Callers still get the throw and
        // show their own UI. The api side already drops 4xx in its own
        // errorMiddleware — this mirrors that on the client. The 4xx arm is
        // narrowed to axios errors so non-axios throws inside the try (e.g.
        // resolveApiBase) still report; isOfflineError covers the transient
        // ones that never reach axios, i.e. the Firebase Auth token refresh
        // failing on a device that dropped the network.
        const status = get(e, 'response.status');
        const isAxios = axios.isAxiosError?.(e) || e?.isAxiosError === true;
        const isExpected = isOfflineError(e) || serverCode === MAINTENANCE_CODE
            || (isAxios && (!status || (status >= 400 && status < 500)));
        if (!isExpected) {
            reportError(error, {
                context: 'callFunction',
                functionName: path,
                params: JSON.stringify(redactSensitive(params)),
                statusCode: status
            });
        }

        throw error;
    }
};

export const uploadImage = async (path, file, options = {}) => {
    assertUploadWithinLimit(file);
    const opts = typeof options === 'boolean' ? {multiple: options} : options;
    const {multiple = false} = opts;
    return callFunction('uploadImage', {path, file, multiple}, {
        headers: {
            'Content-Type': 'multipart/form-data'
        }
    });
};

export const uploadFile = async (path, file, grouping, single = false, extra = {}) => {
    assertUploadWithinLimit(file);
    // `onProgress` is a client-only callback (0..1) for upload UIs — pull it out
    // of the multipart fields and wire it to axios' onUploadProgress.
    const {onProgress, ...fields} = extra;
    return callFunction('uploadFile', {path, file, grouping, single, ...fields}, {
        headers: {
            'Content-Type': 'multipart/form-data'
        },
        ...(onProgress && {
            onUploadProgress: e => {
                if (e.total) onProgress(e.loaded / e.total);
            }
        })
    });
};

// Uploads a batch of freshly-picked files one at a time, tolerating per-file
// failures instead of abandoning the rest of the batch. Each upload commits its
// own arrayUnion server-side, so the old fail-fast `for … await uploadFile()`
// loop persisted files 1..N-1 and silently dropped N.. the moment one upload
// threw — a single slow/failed PDF left exactly one file saved (#1374). Returns
// the picks that uploaded and those that failed so the caller can report the
// failures and keep the failed picks for retry. Entries without a `.file`
// (already-persisted) are skipped.
export const uploadFiles = async (path, fileUploads, upload = uploadFile) => {
    const uploaded = [];
    const failed = [];

    for (const file of fileUploads) {
        if (!file.file) {
            continue;
        }

        try {
            const result = await upload(path, file);
            uploaded.push({file, result});
        } catch(error) {
            failed.push({file, error});
        }
    }

    return {uploaded, failed};
};

// Uploads a paper pre-trip scan through the Admin-SDK api (client Storage writes
// are rejected by storage.rules — see #1157). Returns the server-owned file
// descriptor, which the saved check retains after extraction.
export const uploadPretripImage = async (apparatusId, checkId, file) => {
    assertUploadWithinLimit(file);
    return callFunction('uploadPretripImage', {apparatusId, checkId, file}, {
        headers: {
            'Content-Type': 'multipart/form-data'
        }
    });
};

export const rankComparator = (v1, v2) => {
    if (typeof v1 === 'string') {
        return Object.keys(Ranks).indexOf(v2) - Object.keys(Ranks).indexOf(v1);
    }

    const {role: v1Role} = v1 || {};
    const {role: v2Role} = v2 || {};

    if (!v1Role && !v2Role) {
        return 0;
    }

    return Object.keys(Ranks).indexOf(v2Role) - Object.keys(Ranks).indexOf(v1Role);
};
